import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminPrefetch } from "./useAdminPrefetch";
import { useRequestPrefetch } from "./useRequestPrefetch";

interface UseHoverPrefetchOptions {
    delay?: number;
    enabled?: boolean;
    userId?: number | null; // ← move userId into options
}

/**
 * Custom hook for implementing hover-based route prefetching
 * Provides optimized prefetching with debouncing to improve navigation performance
 */
export const useHoverPrefetch = (
    href: string,
    options: UseHoverPrefetchOptions = {}
) => {
    const { delay = 200, enabled = true, userId = null } = options;
    const router = useRouter();
    const queryClient = useQueryClient();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prefetchedRef = useRef<boolean>(false);
    const { prefetchAdminPage } = useAdminPrefetch();
    const { prefetchRequestView } = useRequestPrefetch(userId ?? null);

    // If href or userId changes, allow prefetch again
    useEffect(() => {
        prefetchedRef.current = false;
    }, [href, userId]);

    const handleMouseEnter = useCallback(() => {
        if (!enabled || !href) return;

        // Check current cache state before deciding to skip
        if (href.includes("/admin/manage-")) {
            const hasCachedData = href.includes("department")
                ? queryClient.getQueryData(["departments"])
                : href.includes("organization")
                  ? queryClient.getQueryData(["organizations"])
                  : href.includes("location")
                    ? queryClient.getQueryData(["locationList"])
                    : false;

            if (!hasCachedData) {
                prefetchedRef.current = false;
            }
        }

        // Check request cache state
        if (href.includes("/request/manage/")) {
            // Extract request ID from URL like /request/manage/123
            const requestIdMatch = href.match(/\/request\/manage\/(\d+)/);
            if (requestIdMatch) {
                const requestId = parseInt(requestIdMatch[1], 10);

                // Include userId in the key only if we have one
                const key = userId
                    ? ["requestViewDetails", requestId, userId]
                    : ["requestViewDetails", requestId];

                const hasCachedData = queryClient.getQueryData(key);

                if (!hasCachedData) {
                    prefetchedRef.current = false;
                }
            }
        }

        if (prefetchedRef.current) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(async () => {
            try {
                // Prefetch the route itself
                router.prefetch(href);

                // Admin page data prefetch
                if (href.includes("/admin/manage-")) {
                    if (href.includes("department")) {
                        await prefetchAdminPage("department");
                    } else if (href.includes("organization")) {
                        await prefetchAdminPage("organization");
                    } else if (href.includes("location")) {
                        await prefetchAdminPage("location");
                    }
                }

                // Request manage page data prefetch
                if (href.includes("/request/manage/")) {
                    const requestIdMatch = href.match(
                        /\/request\/manage\/(\d+)/
                    );
                    if (requestIdMatch) {
                        const requestId = parseInt(requestIdMatch[1], 10);
                        await prefetchRequestView(requestId); // this already uses userId captured in the hook
                    }
                }

                prefetchedRef.current = true;
            } catch (error) {
                console.warn(`Failed to prefetch ${href}:`, error);
            }
        }, delay);
    }, [
        href,
        delay,
        enabled,
        router,
        prefetchAdminPage,
        prefetchRequestView,
        queryClient,
        userId,
    ]);

    const handleMouseLeave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const cleanup = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    }, []);

    return {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        cleanup,
    };
};

/**
 * Hook specifically for admin page prefetching
 * Pre-configured with admin-specific optimizations
 */
export const useAdminHoverPrefetch = (href: string, userId?: number | null) => {
    return useHoverPrefetch(href, {
        delay: 200,
        enabled: href?.includes("/admin/"),
        userId: userId ?? null,
    });
};

/**
 * Hook specifically for request page prefetching
 * Pre-configured with request-specific optimizations
 */
export const useRequestHoverPrefetch = (
    href: string,
    userId?: number | null
) => {
    return useHoverPrefetch(href, {
        delay: 150,
        enabled: href?.includes("/request/manage/"),
        userId: userId ?? null,
    });
};
